import { createLeaderFollowerStore } from 'collaboration-sidebar-common/store/leaderFollowerStore';
import { COMMENT_FILTER_VALUES } from '../constants/commentFilterConstants';
import { VALUES_OF_TABS } from '../constants/tabs';
import { getCommentIdParam } from '../utils/urls';
import { createModuleRepliesMap } from '../utils/commentingUtils';
import { postMessageStrategy } from 'collaboration-sidebar-common/store/postMessageStrategy';
import { CollaborationSidebarSessionId } from 'collaboration-sidebar-common/utils/magpie';
import * as t from 'collaboration-sidebar-common/utils/schema';
import { PLACEMENTS } from 'foundations-components/transitional/components/Tooltip';
import { getInitialCollaborationSidebarState } from '../utils';
import Raven from 'raven-js';
export const commentSchema = t.and(t.object({
  id: t.string,
  createdAt: t.string,
  updatedAt: t.string,
  type: t.union(t.literal('COMMENT'), t.literal('REPLY'), t.literal('DEEP_COMMENT')),
  archived: t.boolean,
  atMentionedUserIds: t.array(t.number),
  message: t.string,
  resolved: t.boolean,
  userId: t.number,
  read: t.boolean,
  parentId: t.nullable(t.string),
  archivedAt: t.nullable(t.string)
}), t.partialObject({
  commentLocation: t.nullable(t.string),
  parentCommentId: t.nullable(t.number),
  metaDescription: t.nullable(t.and(t.object({
    translationKey: t.string,
    locationPath: t.string
  }), t.partialObject({
    options: t.any
  }))),
  attachmentIds: t.array(t.string)
}));
export const replyCommentSchema = t.and(commentSchema, t.object({
  parentId: t.string
}));
export const tabSchema = t.union(...VALUES_OF_TABS.map(t.literal));
export const featuresSchema = t.array(t.union(tabSchema, t.literal('deepCommenting')));
export const selectedAssetObjectSchema = t.object({
  objectId: t.union(t.string, t.number),
  objectType: t.string
});
export const taskStatusSchema = t.union(t.literal('ALL'), t.literal('DUE_TODAY'), t.literal('OVERDUE'), t.literal('ALL_COMPLETED'));
export const tasksFiltersSchema = t.partialObject({
  initialCampaignGuid: t.string,
  campaignEditable: t.boolean,
  initialAssigneeIsCurrentUser: t.boolean,
  marketingStudioCardId: t.string,
  initialTaskStatus: taskStatusSchema
});
export const tasksFormSchema = t.partialObject({
  showTimeField: t.boolean,
  initialCampaignGuid: t.string,
  campaignEditable: t.boolean,
  initialAssigneeIsCurrentUser: t.boolean,
  marketingStudioCardId: t.string
});
export const tasksConfigSchema = t.partialObject({
  lockToCampaign: t.string,
  filters: tasksFiltersSchema,
  form: tasksFormSchema
});
export const commentsFilterSchema = t.union(t.literal('open'), t.literal('all'), t.literal('resolved'), t.literal('unread'));
export const replyCountSchema = t.object({
  replyCount: t.number
});
export const embeddedCommentCoordinatesSchema = t.object({
  top: t.number,
  left: t.number,
  width: t.number,
  height: t.number
});
export const embeddedMetaDescriptionSchema = t.and(t.object({
  translationKey: t.string
}), t.partialObject({
  options: t.any
}));
export const commentableAreaClickCoordinatesSchema = t.object({
  x: t.number,
  y: t.number
});
export const deleteCommentRequestSchema = t.object({
  loading: t.boolean,
  error: t.boolean
});
export const modulesWithRepliesSchema = t.record(t.string, commentSchema);
export const embeddedPopoverPlacementSchema = t.union(...PLACEMENTS.map(placement => t.literal(placement)));
const requiredCollaborationSidebarStateProperties = {
  isOpen: t.boolean,
  tab: tabSchema,
  SELECTED_ASSET: t.nullable(selectedAssetObjectSchema),
  commentThread: t.union(t.string, t.literal(null)),
  hideEmbeddedComments: t.boolean,
  appName: t.union(t.string, t.literal(null)),
  objectId: t.union(t.string, t.number, t.literal(null)),
  objectType: t.union(t.string, t.literal(null)),
  ctaUrl: t.union(t.string, t.literal(null)),
  modulesWithReplies: modulesWithRepliesSchema,
  replyCounts: t.record(t.string, replyCountSchema),
  isEmbeddedCommentingEnabled: t.boolean,
  isCommentModeActive: t.boolean,
  broadcastCommentMessage: t.any,
  commentsFilter: commentsFilterSchema,
  notificationSettingsOpen: t.boolean,
  unreadRepliesMap: t.record(t.string, t.array(commentSchema)),
  isEmbeddedPopoverOpen: t.boolean,
  embeddedCommentCoordinates: embeddedCommentCoordinatesSchema,
  isEmbeddedOpenFromCommentMode: t.boolean,
  deleteCommentRequest: deleteCommentRequestSchema
};
const optionalCollaborationSidebarStateProperties = {
  assetsLastUpdated: t.record(t.string, t.nullable(t.string)),
  enabledFeatures: t.nullable(featuresSchema),
  embeddedPopoverPlacement: embeddedPopoverPlacementSchema,
  embeddedMetaDescription: embeddedMetaDescriptionSchema,
  embeddedModuleID: t.string,
  embeddedPopoverAnchorSelector: t.string,
  tasksConfig: tasksConfigSchema,
  commentableAreaClickCoordinates: commentableAreaClickCoordinatesSchema,
  ctaCommentId: t.nullable(t.string),
  hasUnrecoverableError: t.boolean
};
export const collaborationSidebarStateSchema = t.and(t.object(requiredCollaborationSidebarStateProperties), t.partialObject(optionalCollaborationSidebarStateProperties));
export const partialCollaborationSidebarStateSchema = t.partialObject(Object.assign({}, requiredCollaborationSidebarStateProperties, optionalCollaborationSidebarStateProperties));
export const createTasksConfig = ({
  baseConfig,
  overrides = {}
} = {}) => {
  var _overrides$lockToCamp, _overrides$filters$in, _overrides$filters, _baseConfig$filters, _overrides$filters$ca, _overrides$filters2, _baseConfig$filters2, _overrides$filters$in2, _overrides$filters3, _baseConfig$filters3, _overrides$filters$in3, _overrides$filters4, _baseConfig$filters4, _overrides$filters$ma, _overrides$filters5, _baseConfig$filters5, _overrides$form$showT, _overrides$form, _baseConfig$form, _overrides$form$initi, _overrides$form2, _baseConfig$form2, _overrides$form$campa, _overrides$form3, _baseConfig$form3, _overrides$form$initi2, _overrides$form4, _baseConfig$form4, _overrides$form$marke, _overrides$form5, _baseConfig$form5;
  return {
    lockToCampaign: (_overrides$lockToCamp = overrides === null || overrides === void 0 ? void 0 : overrides.lockToCampaign) !== null && _overrides$lockToCamp !== void 0 ? _overrides$lockToCamp : baseConfig === null || baseConfig === void 0 ? void 0 : baseConfig.lockToCampaign,
    filters: {
      initialCampaignGuid: (_overrides$filters$in = overrides === null || overrides === void 0 || (_overrides$filters = overrides.filters) === null || _overrides$filters === void 0 ? void 0 : _overrides$filters.initialCampaignGuid) !== null && _overrides$filters$in !== void 0 ? _overrides$filters$in : baseConfig === null || baseConfig === void 0 || (_baseConfig$filters = baseConfig.filters) === null || _baseConfig$filters === void 0 ? void 0 : _baseConfig$filters.initialCampaignGuid,
      campaignEditable: (_overrides$filters$ca = overrides === null || overrides === void 0 || (_overrides$filters2 = overrides.filters) === null || _overrides$filters2 === void 0 ? void 0 : _overrides$filters2.campaignEditable) !== null && _overrides$filters$ca !== void 0 ? _overrides$filters$ca : baseConfig === null || baseConfig === void 0 || (_baseConfig$filters2 = baseConfig.filters) === null || _baseConfig$filters2 === void 0 ? void 0 : _baseConfig$filters2.campaignEditable,
      initialAssigneeIsCurrentUser: (_overrides$filters$in2 = overrides === null || overrides === void 0 || (_overrides$filters3 = overrides.filters) === null || _overrides$filters3 === void 0 ? void 0 : _overrides$filters3.initialAssigneeIsCurrentUser) !== null && _overrides$filters$in2 !== void 0 ? _overrides$filters$in2 : baseConfig === null || baseConfig === void 0 || (_baseConfig$filters3 = baseConfig.filters) === null || _baseConfig$filters3 === void 0 ? void 0 : _baseConfig$filters3.initialAssigneeIsCurrentUser,
      initialTaskStatus: (_overrides$filters$in3 = overrides === null || overrides === void 0 || (_overrides$filters4 = overrides.filters) === null || _overrides$filters4 === void 0 ? void 0 : _overrides$filters4.initialTaskStatus) !== null && _overrides$filters$in3 !== void 0 ? _overrides$filters$in3 : baseConfig === null || baseConfig === void 0 || (_baseConfig$filters4 = baseConfig.filters) === null || _baseConfig$filters4 === void 0 ? void 0 : _baseConfig$filters4.initialTaskStatus,
      marketingStudioCardId: (_overrides$filters$ma = overrides === null || overrides === void 0 || (_overrides$filters5 = overrides.filters) === null || _overrides$filters5 === void 0 ? void 0 : _overrides$filters5.marketingStudioCardId) !== null && _overrides$filters$ma !== void 0 ? _overrides$filters$ma : baseConfig === null || baseConfig === void 0 || (_baseConfig$filters5 = baseConfig.filters) === null || _baseConfig$filters5 === void 0 ? void 0 : _baseConfig$filters5.marketingStudioCardId
    },
    form: {
      showTimeField: (_overrides$form$showT = overrides === null || overrides === void 0 || (_overrides$form = overrides.form) === null || _overrides$form === void 0 ? void 0 : _overrides$form.showTimeField) !== null && _overrides$form$showT !== void 0 ? _overrides$form$showT : baseConfig === null || baseConfig === void 0 || (_baseConfig$form = baseConfig.form) === null || _baseConfig$form === void 0 ? void 0 : _baseConfig$form.showTimeField,
      initialCampaignGuid: (_overrides$form$initi = overrides === null || overrides === void 0 || (_overrides$form2 = overrides.form) === null || _overrides$form2 === void 0 ? void 0 : _overrides$form2.initialCampaignGuid) !== null && _overrides$form$initi !== void 0 ? _overrides$form$initi : baseConfig === null || baseConfig === void 0 || (_baseConfig$form2 = baseConfig.form) === null || _baseConfig$form2 === void 0 ? void 0 : _baseConfig$form2.initialCampaignGuid,
      campaignEditable: (_overrides$form$campa = overrides === null || overrides === void 0 || (_overrides$form3 = overrides.form) === null || _overrides$form3 === void 0 ? void 0 : _overrides$form3.campaignEditable) !== null && _overrides$form$campa !== void 0 ? _overrides$form$campa : baseConfig === null || baseConfig === void 0 || (_baseConfig$form3 = baseConfig.form) === null || _baseConfig$form3 === void 0 ? void 0 : _baseConfig$form3.campaignEditable,
      initialAssigneeIsCurrentUser: (_overrides$form$initi2 = overrides === null || overrides === void 0 || (_overrides$form4 = overrides.form) === null || _overrides$form4 === void 0 ? void 0 : _overrides$form4.initialAssigneeIsCurrentUser) !== null && _overrides$form$initi2 !== void 0 ? _overrides$form$initi2 : baseConfig === null || baseConfig === void 0 || (_baseConfig$form4 = baseConfig.form) === null || _baseConfig$form4 === void 0 ? void 0 : _baseConfig$form4.initialAssigneeIsCurrentUser,
      marketingStudioCardId: (_overrides$form$marke = overrides === null || overrides === void 0 || (_overrides$form5 = overrides.form) === null || _overrides$form5 === void 0 ? void 0 : _overrides$form5.marketingStudioCardId) !== null && _overrides$form$marke !== void 0 ? _overrides$form$marke : baseConfig === null || baseConfig === void 0 || (_baseConfig$form5 = baseConfig.form) === null || _baseConfig$form5 === void 0 ? void 0 : _baseConfig$form5.marketingStudioCardId
    }
  };
};
export const createCollaborationSidebarState = overrides => {
  var _overrides$modulesWit, _overrides$unreadRepl;
  return Object.assign({
    isOpen: false,
    tab: 'comments',
    SELECTED_ASSET: null,
    commentThread: null,
    hideEmbeddedComments: false,
    enabledFeatures: [],
    appName: null,
    objectId: null,
    objectType: null,
    ctaUrl: null,
    replyCounts: {},
    isEmbeddedCommentingEnabled: false,
    isCommentModeActive: false,
    broadcastCommentMessage: null,
    commentsFilter: COMMENT_FILTER_VALUES.open,
    ctaCommentId: getCommentIdParam(),
    notificationSettingsOpen: false,
    isEmbeddedPopoverOpen: false,
    embeddedPopoverPlacement: 'top',
    embeddedCommentCoordinates: {
      top: 0,
      left: 0,
      width: 0,
      height: 0
    },
    embeddedModuleID: undefined,
    isEmbeddedOpenFromCommentMode: false,
    embeddedPopoverAnchorSelector: undefined,
    commentableAreaClickCoordinates: undefined,
    deleteCommentRequest: {
      loading: false,
      error: false
    }
  }, overrides, {
    modulesWithReplies: (_overrides$modulesWit = overrides === null || overrides === void 0 ? void 0 : overrides.modulesWithReplies) !== null && _overrides$modulesWit !== void 0 ? _overrides$modulesWit : {},
    unreadRepliesMap: (_overrides$unreadRepl = overrides === null || overrides === void 0 ? void 0 : overrides.unreadRepliesMap) !== null && _overrides$unreadRepl !== void 0 ? _overrides$unreadRepl : {},
    tasksConfig: createTasksConfig({
      overrides: overrides === null || overrides === void 0 ? void 0 : overrides.tasksConfig
    })
  });
};
const setIfDefined = (state, key, value) => {
  if (value === undefined) {
    return;
  }
  state[key] = value;
};
const collaborationSidebarStateKey = `collaboration-sidebar-state-${CollaborationSidebarSessionId}`;
const collaborationSidebarStateActionsFactory = set => {
  return {
    openSidebar: (tab, options) => set(draft => {
      draft.isOpen = true;
      draft.tab = tab !== null && tab !== void 0 ? tab : 'comments';
      setIfDefined(draft, 'SELECTED_ASSET', options === null || options === void 0 ? void 0 : options.selectedAsset);
    }),
    changeTab: tab => set({
      tab
    }),
    setIsCommentModeActive: active => set({
      isCommentModeActive: active
    }),
    broadcastCommentMessage: message => set({
      broadcastCommentMessage: message
    }),
    deleteCommentRequestSuccess: () => set(draft => {
      draft.deleteCommentRequest = {
        loading: false,
        error: false
      };
    }),
    mergeModulesWithRepliesEntry: (moduleId, comment) => set(draft => {
      draft.modulesWithReplies = Object.assign({}, draft.modulesWithReplies, {
        [moduleId]: comment
      });
    }),
    removeModulesWithRepliesEntry: moduleId => set(draft => {
      if (moduleId) {
        delete draft.modulesWithReplies[moduleId];
      }
    }),
    incrementReplyCountBy: (parentCommentId, by) => set(draft => {
      var _draft$replyCounts$pa, _draft$replyCounts$pa2;
      const currentReplyCount = (_draft$replyCounts$pa = (_draft$replyCounts$pa2 = draft.replyCounts[parentCommentId]) === null || _draft$replyCounts$pa2 === void 0 ? void 0 : _draft$replyCounts$pa2.replyCount) !== null && _draft$replyCounts$pa !== void 0 ? _draft$replyCounts$pa : 0;
      draft.replyCounts[parentCommentId] = {
        replyCount: Math.max(currentReplyCount + by, 0)
      };
    }),
    closeEmbeddedPopover: () => set(draft => {
      draft.isEmbeddedPopoverOpen = false;
      draft.commentableAreaClickCoordinates = undefined;
    }),
    openComments: ({
      isCommentModeActive,
      commentThread,
      ctaCommentId,
      SELECTED_ASSET,
      commentsFilter
    } = {}) => set(draft => {
      draft.isOpen = true;
      draft.tab = 'comments';
      setIfDefined(draft, 'isCommentModeActive', isCommentModeActive);
      setIfDefined(draft, 'commentsFilter', commentsFilter);
      setIfDefined(draft, 'commentThread', commentThread);
      setIfDefined(draft, 'ctaCommentId', ctaCommentId);
      setIfDefined(draft, 'SELECTED_ASSET', SELECTED_ASSET);
    }),
    updateCommentThread: commentThread => set({
      commentThread
    }),
    clearCommentThread: () => set({
      commentThread: null
    }),
    maybeClearCommentThreadIfMatches: commentThread => set(draft => {
      if (draft.commentThread === commentThread) {
        draft.commentThread = null;
      }
    }),
    clearCtaCommentId: () => set({
      ctaCommentId: null
    }),
    openTasks: () => set({
      isOpen: true,
      tab: 'tasks',
      isCommentModeActive: false
    }),
    openCalendar: () => set({
      isOpen: true,
      tab: 'calendar',
      isCommentModeActive: false
    }),
    openApprovals: () => set({
      isOpen: true,
      tab: 'approvals',
      isCommentModeActive: false
    }),
    openCopilot: () => set({
      isOpen: true,
      tab: 'copilot',
      isCommentModeActive: false
    }),
    openActivityFeed: () => set({
      isOpen: true,
      tab: 'activityFeed',
      isCommentModeActive: false
    }),
    openReview: () => set({
      isOpen: true,
      tab: 'review',
      isCommentModeActive: false
    }),
    closeSidebar: ({
      disableCommentMode
    } = {}) => set(draft => {
      draft.isOpen = false;
      if (disableCommentMode !== undefined) {
        draft.isCommentModeActive = !disableCommentMode;
      }
    }),
    onUpdateCollaborationSidebarState: updates => set(state => Object.assign({}, state, updates)),
    persistPropsToCollaborationSidebarState: ({
      isEmbeddedCommentingEnabled,
      appName,
      objectId,
      objectType,
      ctaUrl,
      enabledFeatures,
      tasksConfig
    }) => set(draft => {
      setIfDefined(draft, 'isEmbeddedCommentingEnabled', isEmbeddedCommentingEnabled);
      setIfDefined(draft, 'appName', appName);
      setIfDefined(draft, 'objectId', objectId);
      setIfDefined(draft, 'objectType', objectType);
      setIfDefined(draft, 'ctaUrl', ctaUrl);
      setIfDefined(draft, 'enabledFeatures', enabledFeatures);
      draft.tasksConfig = createTasksConfig({
        baseConfig: draft.tasksConfig,
        overrides: tasksConfig
      });
    }),
    enableCommentMode: () => set({
      isOpen: false,
      isCommentModeActive: true
    }),
    disableCommentMode: () => set({
      isCommentModeActive: false
    }),
    selectAsset: asset => set({
      SELECTED_ASSET: asset
    }),
    clearSelectedAsset: () => set({
      SELECTED_ASSET: null
    }),
    updateUnreadReplies: unreadComments => set(draft => {
      draft.unreadRepliesMap = createModuleRepliesMap(unreadComments);
    }),
    setModulesWithReplies: modulesWithReplies => set(draft => {
      draft.modulesWithReplies = modulesWithReplies;
    }),
    mergeReplyCounts: replyCounts => set(draft => {
      draft.replyCounts = Object.assign({}, draft.replyCounts, replyCounts);
    }),
    setHideEmbeddedComments: hideEmbeddedComments => set(draft => {
      draft.hideEmbeddedComments = hideEmbeddedComments;
    }),
    filterComments: filter => set({
      commentsFilter: filter
    }),
    startDeleteCommentRequest: () => set(draft => {
      draft.deleteCommentRequest = {
        loading: true,
        error: false
      };
    }),
    deleteCommentRequestFailed: () => set(draft => {
      draft.deleteCommentRequest = {
        loading: false,
        error: true
      };
    }),
    setHasUnrecoverableError: value => set({
      hasUnrecoverableError: value
    })
  };
};
export const {
  makeLeader: makeCollaborationSidebarStateLeader,
  makeFollower: makeCollaborationSidebarStateFollower
} = createLeaderFollowerStore({
  syncStrategyFactory: postMessageStrategy({
    key: collaborationSidebarStateKey,
    valueSchema: collaborationSidebarStateSchema
  }),
  key: collaborationSidebarStateKey,
  actions: collaborationSidebarStateActionsFactory
});
export const createCollaborationSidebarStateFollower = (overrides, options) => {
  return makeCollaborationSidebarStateFollower(createCollaborationSidebarState(Object.assign({}, getInitialCollaborationSidebarState(), overrides)), options);
};

// Wraps the postMessage follower with a readyOutcome promise that resolves
// on first sync or times out after FOLLOWER_READY_TIMEOUT_MS.
export const createCollaborationSidebarStateEmbeddedFollower = overrides => {
  const initialState = createCollaborationSidebarState(Object.assign({}, getInitialCollaborationSidebarState(), overrides));
  const startTime = performance.now();
  let resolveReadyOutcome;
  const readyOutcome = new Promise(resolve => {
    resolveReadyOutcome = resolve;
  });
  let ready = false;
  const FOLLOWER_READY_TIMEOUT_MS = 30000;
  let timeoutLogged = false;
  const onReadyTimeout = () => {
    if (!ready && !timeoutLogged) {
      timeoutLogged = true;
      Raven.captureException(new Error(`createCollaborationSidebarStateEmbeddedFollower timed out after ${FOLLOWER_READY_TIMEOUT_MS}ms: no leader responded`), {
        fingerprint: ['createCollaborationSidebarStateEmbeddedFollower timed out']
      });
      resolveReadyOutcome({
        outcome: 'timeout'
      });
    }
  };
  const follower = makeCollaborationSidebarStateFollower(initialState, {
    onReadyTimeout,
    readyTimeout: FOLLOWER_READY_TIMEOUT_MS
  });
  void follower.isReady.then(() => {
    if (!ready) {
      ready = true;
      resolveReadyOutcome({
        outcome: 'success',
        durationMs: performance.now() - startTime
      });
    }
  });
  return {
    getState: follower.getState,
    getInitialState: () => initialState,
    subscribe: follower.subscribe,
    isReady: follower.isReady,
    getIsReady: () => ready,
    readyOutcome,
    cleanup: follower.cleanup,
    actions: follower.actions
  };
};
const WINDOW_FOLLOWER_KEY = '__collaborationSidebarStateFollower__';
const getWindowWithCachedFollower = () => window;
export const getWindowCachedFollower = () => {
  const win = getWindowWithCachedFollower();
  if (!win[WINDOW_FOLLOWER_KEY]) {
    win[WINDOW_FOLLOWER_KEY] = createCollaborationSidebarStateFollower();
  }
  return win[WINDOW_FOLLOWER_KEY];
};
export const collaborationSidebarStateFollowerReady = async () => {
  const follower = getWindowCachedFollower();
  await follower.isReady;
  return follower;
};
export const resetCollaborationSidebarStateFollowerForTesting = () => {
  const win = getWindowWithCachedFollower();
  if (win[WINDOW_FOLLOWER_KEY]) {
    win[WINDOW_FOLLOWER_KEY].cleanup();
    win[WINDOW_FOLLOWER_KEY] = undefined;
  }
};
export const reportCollaborationSidebarUnrecoverableError = () => {
  void collaborationSidebarStateFollowerReady().then(follower => {
    follower.actions.setHasUnrecoverableError(true);
  });
};